/**
 * Donut SVG — port 1:1 z js/analytics.js renderDonutChart.
 * Kliknięcie segmentu/wiersza legendy → onPick(categoryId) (drill-down).
 * Empty state: pusty pierścień + tekst.
 */
import { useMemo } from 'react';
import { formatCurrency, safeColor } from '../../lib/utils';
import { getCategoryIcon } from '../../lib/categoryIcons';
import type { CategoryBreakdownRow } from '../../features/analytics/selectors';

interface Props {
  rows: CategoryBreakdownRow[];
  total: number;
  mode: 'income' | 'expense';
  onPick?: (categoryId: string) => void;
}

const CX = 160;
const CY = 160;
const R_OUTER = 110;
const R_INNER = 72;
// Outside-icons (port z legacy js/analytics.js renderDonutChart)
const R_LINE_START = R_OUTER + 4; // 114 — koniec segmentu, start kreski
const R_LINE_END = R_OUTER + 22; // 132 — koniec kreski, blisko ikony
const R_ICON = 152; // środek ikonki
const ICON_BOX = 30; // bbox ikony (foreignObject 30x30)
const SKIP_ICON_DEG = 10; // segmenty < 10° pomijają ikonę (zlewałyby się)

function polarToXY(r: number, deg: number): { x: number; y: number } {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

function buildArcPath(rOuter: number, rInner: number, startDeg: number, endDeg: number, gap: number): string {
  const adjEnd = endDeg - gap;
  const s = polarToXY(rOuter, startDeg);
  const e = polarToXY(rOuter, adjEnd);
  const si = polarToXY(rInner, startDeg);
  const ei = polarToXY(rInner, adjEnd);
  const large = adjEnd - startDeg > 180 ? 1 : 0;
  return [
    `M ${s.x.toFixed(2)} ${s.y.toFixed(2)}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`,
    `L ${ei.x.toFixed(2)} ${ei.y.toFixed(2)}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${si.x.toFixed(2)} ${si.y.toFixed(2)}`,
    `Z`,
  ].join(' ');
}

export function Donut({ rows, total, mode, onPick }: Props) {
  const segments = useMemo(() => {
    if (total === 0) return [];
    const gap = rows.length > 1 ? 0.5 : 0;
    let start = 0;
    return rows.map((r) => {
      const seg = (r.sum / total) * 360;
      const end = start + seg;
      const mid = start + seg / 2;
      const path = buildArcPath(R_OUTER, R_INNER, start, end, gap);
      const showIcon = seg >= SKIP_ICON_DEG;
      const lineStart = polarToXY(R_LINE_START, mid);
      const lineEnd = polarToXY(R_LINE_END, mid);
      const iconCenter = polarToXY(R_ICON, mid);
      start = end;
      return { row: r, path, midDeg: mid, segDeg: seg, showIcon, lineStart, lineEnd, iconCenter };
    });
  }, [rows, total]);

  const isExpense = mode === 'expense';
  const centerColor = isExpense ? 'rgb(244 63 94)' : 'rgb(16 185 129)';
  const centerLabel = isExpense ? 'Wydatki' : 'Przychody';

  if (segments.length === 0) {
    return (
      <div className="flex flex-col items-center py-4">
        <svg viewBox="0 0 320 320" width="100%" style={{ maxWidth: 280 }}>
          <circle cx={CX} cy={CY} r={R_OUTER - 5} fill="none" stroke="currentColor" strokeWidth="38" className="text-slate-200 dark:text-slate-700" />
          <text x={CX} y={CY - 6} textAnchor="middle" fontSize="13" className="fill-slate-400">Brak danych</text>
          <text x={CX} y={CY + 14} textAnchor="middle" fontSize="11" className="fill-slate-400">w wybranym zakresie</text>
        </svg>
      </div>
    );
  }

  return (
    <div>
      <svg
        viewBox="0 0 320 320"
        width="100%"
        // overflow:visible — ikony przy R_ICON=152 (z bbox 30) sięgają poza
        // viewBox 320×320; ratuje też procenty pod ikonami.
        style={{ maxWidth: 360, display: 'block', margin: '0 auto', overflow: 'visible' }}
      >
        {/* Segmenty — full circle (1 kategoria 100%) renderujemy jako <circle>
            z stroke = grubość pierścienia. Arc path z start=0 end=360 jest
            degenerate (start point === end point → SVG nie rysuje). */}
        {segments.length === 1 ? (
          <circle
            cx={CX}
            cy={CY}
            r={(R_OUTER + R_INNER) / 2}
            fill="none"
            stroke={safeColor(segments[0]!.row.color, '#9E9E9E')}
            strokeWidth={R_OUTER - R_INNER}
            className={onPick ? 'cursor-pointer transition-opacity hover:opacity-80' : ''}
            onClick={() => onPick?.(segments[0]!.row.categoryId)}
          />
        ) : (
          segments.map(({ row, path }) => (
            <path
              key={row.categoryId}
              d={path}
              fill={safeColor(row.color, '#9E9E9E')}
              className={onPick ? 'cursor-pointer transition-opacity hover:opacity-80' : ''}
              onClick={() => onPick?.(row.categoryId)}
            />
          ))
        )}

        {/* Connector lines (pod ikonami w stack-order) */}
        {segments
          .filter((s) => s.showIcon)
          .map(({ row, lineStart, lineEnd }) => (
            <line
              key={`line-${row.categoryId}`}
              x1={lineStart.x.toFixed(2)}
              y1={lineStart.y.toFixed(2)}
              x2={lineEnd.x.toFixed(2)}
              y2={lineEnd.y.toFixed(2)}
              stroke={safeColor(row.color, '#9E9E9E')}
              strokeOpacity={0.45}
              strokeWidth={1.25}
            />
          ))}

        {/* Ikony kategorii w foreignObject + procent pod nimi */}
        {segments
          .filter((s) => s.showIcon)
          .map(({ row, iconCenter }) => {
            const Icon = getCategoryIcon(row.icon);
            const c = safeColor(row.color, '#9E9E9E');
            return (
              <g
                key={`icon-${row.categoryId}`}
                className={onPick ? 'cursor-pointer' : ''}
                onClick={() => onPick?.(row.categoryId)}
              >
                <foreignObject
                  x={iconCenter.x - ICON_BOX / 2}
                  y={iconCenter.y - ICON_BOX / 2}
                  width={ICON_BOX}
                  height={ICON_BOX}
                  style={{ overflow: 'visible' }}
                >
                  <div
                    style={{
                      width: ICON_BOX,
                      height: ICON_BOX,
                      borderRadius: '9999px',
                      backgroundColor: `${c}22`,
                      color: c,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={16} strokeWidth={2.25} />
                  </div>
                </foreignObject>
                <text
                  x={iconCenter.x}
                  y={iconCenter.y + ICON_BOX / 2 + 10}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="700"
                  fill={c}
                >
                  {row.pct}%
                </text>
              </g>
            );
          })}

        {/* Centrum: label + total */}
        <text x={CX} y={CY - 8} textAnchor="middle" fontSize="11" fontWeight="600" className="fill-slate-500 dark:fill-slate-400">
          {centerLabel}
        </text>
        <text x={CX} y={CY + 16} textAnchor="middle" fontSize="18" fontWeight="800" fill={centerColor}>
          {formatCurrency(total)}
        </text>
      </svg>
      <div className="mt-4 space-y-1.5">
        {rows.map((r) => {
          const Icon = getCategoryIcon(r.icon);
          const color = safeColor(r.color, '#9E9E9E');
          return (
            <button
              key={r.categoryId}
              type="button"
              onClick={() => onPick?.(r.categoryId)}
              className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left"
            >
              <span
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${color}22`, color }}
              >
                <Icon size={18} />
              </span>
              <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{r.name}</span>
              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 tabular-nums">{r.pct}%</span>
              <span className="text-sm font-semibold tabular-nums text-slate-900 dark:text-white">
                {formatCurrency(r.sum)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
